import type { RoomOptions } from '@infiltre/shared';
import { useMemo } from 'react';
import { createOnlineDriver } from '@/app/online-driver';
import { type Identity, type IdentityProvider, type Platform, usePlatform } from '@/platform';
import { useGameStore, useSessionStore } from '@/shared/hooks/use-shallow-store';
import {
  type AnalyticsCreateFailReason,
  type AnalyticsCreateSurface,
  type AnalyticsJoinFailReason,
  type AnalyticsJoinSurface,
  trackEvent,
} from '@/shared/lib/analytics';
import { createApi } from '@/shared/lib/api';
import { DriverMode } from '@/shared/lib/driver';
import { localKvStorage } from '@/shared/lib/storage';
import { createRoomRest, externalJoinRoomRest, joinRoomRest } from '../api/use-rooms';
import { saveName } from './saved-name';

const platformSuffix = (p: Platform): 'discord' | 'web' | 'capacitor' | 'tauri' => p;

const classifyCreateFail = (reason: string): AnalyticsCreateFailReason => {
  if (/network|fetch|timeout|offline/i.test(reason)) return 'network';
  if (/^4\d\d|validation|invalid|bad_request/i.test(reason)) return 'validation';
  if (/^5\d\d|server|internal/i.test(reason)) return 'server';
  return 'unknown';
};

const classifyJoinFail = (reason: string): AnalyticsJoinFailReason => {
  if (/not[_-]?found|404/i.test(reason)) return 'not_found';
  if (/full/i.test(reason)) return 'full';
  if (/in[_-]?progress|started/i.test(reason)) return 'in_progress';
  if (/unauth|401|403/i.test(reason)) return 'unauthorized';
  return 'unknown';
};

/**
 * Reuse an existing session if valid; mint a fresh anon player only when none.
 * Calling `acquire()` unconditionally would issue a new playerId on every
 * action — a refresh on `/room/:code` would auto-join with a brand-new id,
 * leaving the original player in the room as a ghost host.
 */
const ensureIdentity = async (provider: IdentityProvider, nickname: string): Promise<Identity> => {
  const existing = await provider.restore();
  if (existing) return existing;
  return provider.acquire({ nickname });
};

export type UseLobbyActionsOptions = {
  onCreated?: (code: string) => void;
  onJoined?: (code: string) => void;
  /** Channel-bound create-or-join (Discord Activity) — distinct from create/join. */
  onActivityRoomReady?: (code: string) => void;
};

/**
 * Glue: identity → REST create/join → wire `RemoteGameDriver` into store →
 * caller-supplied callback for navigation. Pages own routing.
 */
export const useLobbyActions = (opts: UseLobbyActionsOptions = {}) => {
  const { apiBaseUrl, identity: identityProvider, platform } = usePlatform();
  // Hook-scoped client — avoids top-level singleton frozen at module load.
  const api = useMemo(
    () => createApi(apiBaseUrl, () => identityProvider.getToken()),
    [apiBaseUrl, identityProvider],
  );
  const setMode = useSessionStore((s) => s.setMode);
  const setIdentity = useSessionStore((s) => s.setIdentity);
  const setDriver = useSessionStore((s) => s.setDriver);
  const setRoom = useGameStore((s) => s.setRoom);
  const setRoomCode = useSessionStore((s) => s.setRoomCode);
  const setLobbyError = useSessionStore((s) => s.setLobbyError);
  const setLobbyBusy = useSessionStore((s) => s.setLobbyBusy);

  const createRoom = async (
    name: string,
    args: {
      options?: Partial<RoomOptions>;
      persistName?: boolean;
      surface?: AnalyticsCreateSurface;
    } = {},
  ): Promise<void> => {
    const surface: AnalyticsCreateSurface = args.surface ?? 'home';
    setLobbyBusy(true);
    setLobbyError(null);
    trackEvent({
      name: `room.create_requested_${surface}`,
      data: { gameMode: 'infiltre_classic' },
    });
    try {
      const identity = await ensureIdentity(identityProvider, name);
      const { room } = await createRoomRest(api, {
        name,
        options: args.options,
      });
      setIdentity(identity);
      setMode(DriverMode.Online);
      setRoom(room);
      setRoomCode(room.code);
      const driver = createOnlineDriver(
        room.id,
        () => identityProvider.getToken(),
        apiBaseUrl,
        (token) => identityProvider.setToken(token),
      );
      setDriver(driver);
      // Skip persistence for random guest names.
      if (args.persistName !== false) saveName(localKvStorage, name);
      trackEvent({
        name: `room.create_succeeded_${surface}`,
        data: { roomId: room.id, platform: platformSuffix(platform) },
      });
      opts.onCreated?.(room.code);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'unknown_error';
      const reason = classifyCreateFail(message);
      setLobbyError(message);
      trackEvent({ name: `room.create_failed_${surface}`, data: { reason } });
    } finally {
      setLobbyBusy(false);
    }
  };

  const joinRoom = async (
    code: string,
    name: string,
    callOpts: { persistName?: boolean; surface?: AnalyticsJoinSurface } = {},
  ): Promise<void> => {
    const surface: AnalyticsJoinSurface = callOpts.surface ?? 'manual';
    setLobbyBusy(true);
    setLobbyError(null);
    const upper = code.trim().toUpperCase();
    trackEvent({ name: `room.join_requested_${surface}`, data: {} });
    try {
      const identity = await ensureIdentity(identityProvider, name);
      const { room } = await joinRoomRest(api, { code: upper, name });
      setIdentity(identity);
      setMode(DriverMode.Online);
      setRoom(room);
      setRoomCode(upper);
      const driver = createOnlineDriver(
        room.id,
        () => identityProvider.getToken(),
        apiBaseUrl,
        (token) => identityProvider.setToken(token),
      );
      setDriver(driver);
      // Skip persistence for randomly-generated guest names — only names
      // the user actually picked (Discord identity, saved, in-room edits)
      // should be remembered for the next session.
      if (callOpts.persistName !== false) saveName(localKvStorage, name);
      trackEvent({
        name: `room.join_succeeded_${surface}`,
        data: { roomId: room.id, platform: platformSuffix(platform) },
      });
      opts.onJoined?.(upper);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'unknown_error';
      const reason = classifyJoinFail(message);
      setLobbyError(message);
      trackEvent({ name: `room.join_failed_${surface}`, data: { reason } });
    } finally {
      setLobbyBusy(false);
    }
  };

  /** Discord Activity create-or-join via opaque `externalKey`. */
  const startActivityRoom = async (
    name: string,
    args: {
      externalKey: string;
      options?: Partial<RoomOptions>;
    },
  ): Promise<void> => {
    const surface: AnalyticsCreateSurface = 'discord_activity';
    setLobbyBusy(true);
    setLobbyError(null);
    trackEvent({
      name: `room.create_requested_${surface}`,
      data: { gameMode: 'infiltre_classic' },
    });
    try {
      const identity = await ensureIdentity(identityProvider, name);
      // Discord OAuth returns display name; anon echoes nickname (no-op).
      const resolvedName = identity.name || name;
      const { room } = await externalJoinRoomRest(api, {
        externalKey: args.externalKey,
        name: resolvedName,
        ...(args.options !== undefined ? { options: args.options } : {}),
      });
      setIdentity(identity);
      setMode(DriverMode.Online);
      setRoom(room);
      setRoomCode(room.code);
      const driver = createOnlineDriver(
        room.id,
        () => identityProvider.getToken(),
        apiBaseUrl,
        (token) => identityProvider.setToken(token),
      );
      setDriver(driver);
      saveName(localKvStorage, resolvedName);
      trackEvent({
        name: `room.create_succeeded_${surface}`,
        data: { roomId: room.id, platform: platformSuffix(platform) },
      });
      opts.onActivityRoomReady?.(room.code);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'unknown_error';
      const reason = classifyCreateFail(message);
      setLobbyError(message);
      trackEvent({ name: `room.create_failed_${surface}`, data: { reason } });
    } finally {
      setLobbyBusy(false);
    }
  };

  return { createRoom, joinRoom, startActivityRoom };
};
